import { memo, useEffect, useRef } from 'react';
import { createFooter, initDictionaryCatalog } from '@wg/wows-common-footer/main';

import settings from '~/settings';

import { isWCFRequired } from '~/constants';
import { isChinaRealm } from '~/helpers/realm';

import styles from './Footer.scss';

import type { IFooterProps, Realm } from '@wg/wows-common-footer/types';

const init = async () => {
  const {
    realm: { languageCode, code },
    urlByRealm,
  } = settings;

  await initDictionaryCatalog({
    language: languageCode,
    dictionaryCatalog: window.i18n.catalog,
  });

  const options: IFooterProps = {
    selector: '#footer-container',
    language: languageCode,
    realm: code as Realm,
    version: '',
    dictionaryCatalog: window.i18n.catalog,
    rootUrl: '',
    onLanguageChange: ({ language, realm }) => {
      if (realm === code) {
        const url = new URL(location.href);

        url.searchParams.set('language', language);
        location.assign(url.search);
      } else {
        const url = new URL(location.pathname, urlByRealm[realm]);

        url.searchParams.set('language', language);
        location.assign(url);
      }
    },
    onClickByLink: (event: MouseEvent) => {
      event.preventDefault();
      const target = event.currentTarget as HTMLLinkElement;
      if (target.id !== 'ot-sdk-btn') {
        window.open(target.href, '_blank');
      }
    },
  };

  if (isChinaRealm()) {
    options.realms = [];
  }

  void createFooter(options);

  window.OneTrust?.initializeCookiePolicyHtml?.();
};

const Footer = () => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isWCFRequired) {
      void init();
    }
  }, []);

  useEffect(() => {
    if (!(ref.current instanceof Element)) {
      return;
    }

    const observer = new MutationObserver(() => {
      const element = document.getElementById('ot-sdk-btn');
      if (element && window.OneTrust) {
        observer.disconnect();

        window.OneTrust?.initializeCookiePolicyHtml?.();
      }
    });

    observer.observe(ref.current, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [ref]);

  return isWCFRequired ? <div ref={ref} id="footer-container" className={styles.footerWrapper} /> : null;
};

export default memo(Footer);
